import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, ArrowRight, UserPlus } from 'lucide-react';
import { useOverview, useIntegrity, useItemAnalysis, usePeople, useTests, type OverviewResponse } from 'modules/analytics';
import { usePendingGrading } from 'modules/grading';
import { useFeature, FEATURES } from 'modules/entitlements';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useCreateActions, type CreateAction } from 'components/global/HeaderCreateMenu';
import config from 'config';
import { useAnalyticsFilters } from 'pages/Analytics/useAnalyticsFilters';
import { groupPendingByTest } from 'pages/Analytics/analyticsFormat';
import { M, useMobileCreateSheet } from 'components/mobile';
import { MobileAnalyticsWorkspace } from '../analytics/MobileAnalyticsWorkspace';
import { DemoWorkspaceBanner, useProductTour } from 'modules/tour';

type OnboardingStep = {
  id: 'create-test' | 'add-questions' | 'invite-candidates';
  title: string;
  description: string;
  to: string;
  Icon: React.ElementType;
};

function hasAnalyticsActivity(data: OverviewResponse | undefined, pendingGrading: number): boolean {
  return Boolean(
    data &&
      (pendingGrading > 0 || data.totalAttempts > 0),
  );
}

function findCreateAction(actions: CreateAction[], route: string): CreateAction | undefined {
  return actions.find((action) => action.to === route);
}

function OnboardingPrimaryContent({ step, actionLabel }: { step: OnboardingStep; actionLabel: string }) {
  return (
    <span className="block min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <step.Icon size={18} aria-hidden style={{ color: M.ink3 }} />
        <span className="block text-[16px] font-extrabold leading-snug" style={{ color: M.ink }}>
          {step.title}
        </span>
      </span>
      <span className="mt-1.5 block text-[13px] leading-5" style={{ color: M.ink3 }}>
        {step.description}
      </span>
      <span
        className="mt-4 inline-flex h-11 items-center gap-1.5 rounded-md px-4 text-[13.5px] font-bold"
        style={{ background: M.primary, color: M.primaryInk }}
      >
        {actionLabel}
        <ArrowRight size={15} aria-hidden />
      </span>
    </span>
  );
}

export function DashboardMobile() {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  const orgId = localStorage.getItem(config.api.active_org_key) || authState.organization?.id || undefined;
  const filters = useAnalyticsFilters();
  const hasAdvanced = useFeature(FEATURES.ADVANCED_ANALYTICS);
  const canInviteMembers = usePermission(PERMISSIONS.MEMBERS_INVITE);
  const canManageMembers = usePermission(PERMISSIONS.MEMBERS_EDIT);
  const createActions = useCreateActions();

  const overview = useOverview({ orgId, params: filters.params });
  const effectiveTab = hasAdvanced ? filters.tab : 'overview';
  const integrity = useIntegrity({ orgId, params: filters.params, enabled: hasAdvanced && (effectiveTab === 'overview' || effectiveTab === 'integrity') });
  const tests = useTests({ orgId, params: filters.params, enabled: hasAdvanced && (effectiveTab === 'overview' || effectiveTab === 'tests') });
  const people = usePeople({ orgId, params: filters.peopleParams, enabled: hasAdvanced && effectiveTab === 'people' });
  const items = useItemAnalysis({ orgId, testId: filters.testId, params: filters.params, enabled: hasAdvanced && effectiveTab === 'tests' });
  const grading = usePendingGrading(orgId);

  const d = overview.data;
  const pendingGrading = grading.data?.length ?? 0;
  const pendingGroups = useMemo(() => groupPendingByTest(grading.data), [grading.data]);
  const openCreateSheet = useMobileCreateSheet();
  const { startTour } = useProductTour();
  const createTestAction = findCreateAction(createActions, '/app/tests/create');
  const questionAction = findCreateAction(createActions, '/app/questions/new');
  const documentAction = findCreateAction(createActions, '/app/documents');
  const questionOrDocumentAction = questionAction || documentAction;
  const onboardingSteps: OnboardingStep[] = [
    ...(createTestAction
      ? [
          {
            id: 'create-test' as const,
            title: t('analytics_onboarding_create_title', 'Create your first test'),
            description: t('analytics_onboarding_create_description', 'Start from scratch or use AI to draft questions.'),
            to: createTestAction.to,
            Icon: ClipboardList,
          },
        ]
      : []),
    ...(questionOrDocumentAction
      ? [
          {
            id: 'add-questions' as const,
            title: t(
              documentAction && !questionAction ? 'analytics_onboarding_sources_title' : 'analytics_onboarding_questions_title',
              documentAction && !questionAction ? 'Add source material' : 'Add questions',
            ),
            description: t(
              documentAction && !questionAction
                ? 'analytics_onboarding_sources_description'
                : 'analytics_onboarding_questions_description',
              documentAction && !questionAction
                ? 'Upload material to start building an assessment.'
                : 'Build a question bank or generate with AI.',
            ),
            to: questionOrDocumentAction.to,
            Icon: ClipboardList,
          },
        ]
      : []),
    ...(canInviteMembers && canManageMembers
      ? [
          {
            id: 'invite-candidates' as const,
            title: t('analytics_onboarding_invite_title', 'Invite candidates'),
            description: t('analytics_onboarding_invite_description', 'Bring your team in when your test is ready.'),
            to: '/app/admin/organization/users',
            Icon: UserPlus,
          },
        ]
      : []),
  ];
  const isFirstVisit =
    Boolean(d) &&
    !overview.isLoading &&
    !overview.isError &&
    !grading.isLoading &&
    !grading.isError &&
    !hasAnalyticsActivity(d, pendingGrading);
  if (isFirstVisit) {
    const primaryStep = onboardingSteps[0];

    return (
      <div
        data-mobile-analytics-page
        id="dashboard-overview-card"
        className="-mx-4 -mt-3 min-h-full px-4 pt-3"
      >
        <div className="mb-4">
          <DemoWorkspaceBanner onStartTour={startTour} />
        </div>
        <p className="mt-3 text-[12px] font-medium" style={{ color: M.ink3 }}>
              {t('nav.analytics')}
            </p>
            <h1 className="mt-4 text-[26px] font-extrabold tracking-[-0.03em]" style={{ color: M.ink }}>
              {t('analytics_onboarding_title', 'You’re ready to begin.')}
            </h1>
            <p className="mt-2 max-w-[330px] text-[14px] leading-5" style={{ color: M.ink3 }}>
              {t(
                'analytics_onboarding_intro',
                'Set up your first assessment and insights will start appearing here.',
              )}
            </p>

            <div
              className="mt-6 rounded-lg px-3.5 py-3"
              style={{ background: M.bg, border: `1px solid ${M.border}` }}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-[13px] font-bold" style={{ color: M.ink }}>
                  {t('analytics_onboarding_setup', 'Your workspace setup')}
                </p>
                <span className="text-[12px] font-bold tabular-nums" style={{ color: M.ink }}>
                  {onboardingSteps.length > 0 ? `0 / ${onboardingSteps.length}` : '—'}
                </span>
              </div>
              <div className="mt-2.5 flex gap-1" aria-hidden>
                {onboardingSteps.map((step) => (
                  <span key={step.id} className="h-1 flex-1 rounded-full" style={{ background: M.border }} />
                ))}
              </div>
              <p className="mt-2.5 text-[12px] leading-4" style={{ color: M.ink3 }}>
                {t('analytics_onboarding_setup_hint', 'Complete the steps available to you to unlock insights.')}
              </p>
            </div>

            {primaryStep ? (
              <ol className="mt-5">
                {onboardingSteps.map((step, index) => {
                  const isPrimary = index === 0;
                  const cardClass =
                    'block w-full rounded-lg text-left transition-transform active:scale-[.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
                  const cardStyle = { background: M.card, border: `1px solid ${M.border}` };
                  return (
                    <li key={step.id} className="flex gap-3 pb-3 last:pb-0">
                      <div className="flex flex-col items-center" aria-hidden>
                        <span
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-bold"
                          style={isPrimary ? { background: M.ink, color: '#fff' } : { background: M.muted, color: M.ink3 }}
                        >
                          {index + 1}
                        </span>
                        {index < onboardingSteps.length - 1 && (
                          <span className="mt-1.5 w-px flex-1" style={{ background: M.border }} />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        {isPrimary && step.id === 'create-test' ? (
                          <button
                            type="button"
                            onClick={openCreateSheet}
                            data-onboarding-action={step.id}
                            className={`${cardClass} p-4`}
                            style={cardStyle}
                          >
                            <OnboardingPrimaryContent
                              step={step}
                              actionLabel={t('analytics_onboarding_create_action', 'Create test')}
                            />
                          </button>
                        ) : isPrimary ? (
                          <Link to={step.to} data-onboarding-action={step.id} className={`${cardClass} p-4`} style={cardStyle}>
                            <OnboardingPrimaryContent step={step} actionLabel={step.title} />
                          </Link>
                        ) : (
                          <Link
                            to={step.to}
                            data-onboarding-action={step.id}
                            className={`${cardClass} flex min-h-[64px] items-center gap-3 px-3.5 py-3`}
                            style={cardStyle}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block text-[14px] font-bold" style={{ color: M.ink }}>
                                {step.title}
                              </span>
                              <span className="mt-0.5 block text-[12px] leading-4" style={{ color: M.ink3 }}>
                                {step.description}
                              </span>
                            </span>
                            <ArrowRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} aria-hidden />
                          </Link>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div
                className="mt-5 rounded-lg px-4 py-5 text-[13px] leading-5"
                style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink3 }}
              >
                {t(
                  'analytics_onboarding_no_actions',
                  'Ask an organization admin to give you access to the next setup step.',
                )}
              </div>
            )}

            <p className="mt-5 max-w-[340px] text-[12px] leading-5" style={{ color: M.faint }}>
              {t(
                'analytics_onboarding_reassurance',
                'When candidates respond, you’ll see performance and completion insights here.',
              )}
            </p>
      </div>
    );
  }

  return (
    <div
      data-mobile-analytics-page
      id="dashboard-overview-card"
      className="-mx-4 -mt-3 min-h-full"
    >
      <div className="px-4 pt-3 pb-2">
        <DemoWorkspaceBanner onStartTour={startTour} />
      </div>
      <MobileAnalyticsWorkspace
        filters={filters}
        hasAdvanced={hasAdvanced}
        effectiveTab={effectiveTab}
        overview={overview}
        people={people}
        tests={tests}
        items={items}
        integrity={integrity}
        pendingGroups={pendingGroups}
        pendingLoading={Boolean(grading.isLoading)}
      />
    </div>
  );
}

export default DashboardMobile;
